import React, { useState, useEffect } from "react";
import { authAPI } from "@/services/api.util";
import { useAuthContext } from "@/context/Context";
import { processImageForUpload, removeImage } from "@/services/image.util";

export default function EditProfile({ activeTab }) {
  const { loggedInUser, setLoggedInUser } = useAuthContext();
  const [previewImage, setPreviewImage] = useState(
    "/assets/img/dashboard/edit/1.png"
  );
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    address: "",
    state: "",
    country: "",
    personalInfo: ""
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleImageChange = async (event) => {
    const file = event.target.files[0];

    if (file) {
      try {
        setLoading(true);
        const base64Image = await processImageForUpload(file);
        setPreviewImage(base64Image);
      } catch (error) {
        console.error('Error processing image:', error);
        setMessage({ type: 'error', text: error.message });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getProfile();
      
      if (response.success && response.data.user) {
        const userData = response.data.user;
        setFormData({
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          mobileNumber: userData.mobileNumber || "",
          address: userData.address || "",
          state: userData.state || "",
          country: userData.country || "India",
          personalInfo: userData.personalInfo || ""
        });
        
        if (userData.profileImage) {
          setPreviewImage(userData.profileImage);
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setMessage({ type: 'error', text: 'Failed to load profile data' });
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchUserData();
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const response = await authAPI.updateProfile({
        profileImage: previewImage,
        ...formData
      });
      
      if (response.success) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        
        // Update the user context with new data
        const updatedUserData = {
          ...loggedInUser,
          ...formData,
          profileImage: previewImage
        };
        
        setLoggedInUser(updatedUserData);
        
        // Update localStorage with new user data
        const currentUserData = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedLocalStorageData = {
          ...currentUserData,
          ...formData,
          profileImage: previewImage
        };
        localStorage.setItem('user', JSON.stringify(updatedLocalStorageData));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ 
        type: 'error', 
        text: error.data?.message || 'Failed to update profile' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`tabs__pane -tab-item-1 ${activeTab == 1 ? "is-active" : ""} `}
    >
      <div className="row y-gap-20 x-gap-20 items-center">
        <label
          className="col-auto"
          htmlFor="imageUpload"
          style={
            previewImage
              ? {}
              : { backgroundColor: "#f2f3f4", width: 100, height: 100 }
          }
        >
          {previewImage && (
            <img
              className="size-100"
              src={previewImage}
              alt={previewImage ? "image" : ""}
            />
          )}
        </label>

        <div className="col-auto">
          <div className="text-16 fw-500 text-dark-1">Your avatar</div>
          <div className="text-14 lh-1 mt-10">
            PNG or JPG no bigger than 800px wide and tall.
          </div>

          <div className="d-flex x-gap-10 y-gap-10 flex-wrap pt-15">
            <div>
              <div className="d-flex justify-center items-center size-40 rounded-8 bg-light-3">
                <label
                  style={{ cursor: loading ? "not-allowed" : "pointer" }}
                  htmlFor="imageUpload1"
                  className="icon-cloud text-16"
                ></label>
                <input
                  required
                  id="imageUpload1"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={loading}
                  style={{ display: "none" }}
                />
              </div>
            </div>
            <div>
              <div
                style={{ cursor: "pointer" }}
                onClick={() => {
                  document.getElementById("imageUpload1").value = "";
                  setPreviewImage(removeImage());
                }}
                className="d-flex justify-center items-center size-40 rounded-8 bg-light-3"
              >
                <div className="icon-bin text-16"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-top-light pt-30 mt-30">
        <form onSubmit={handleSubmit} className="contact-form row y-gap-30">
          <div className="col-md-6">
            <label className="text-16 lh-1 fw-500 text-dark-1 mb-10">
              First Name
            </label>

            <input 
              required 
              type="text" 
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              placeholder="First Name" 
            />
          </div>

          <div className="col-md-6">
            <label className="text-16 lh-1 fw-500 text-dark-1 mb-10">
              Last Name
            </label>

            <input 
              required 
              type="text" 
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder="Last Name" 
            />
          </div>

          <div className="col-md-6">
            <label className="text-16 lh-1 fw-500 text-dark-1 mb-10">
              Mobile number
            </label>

            <input 
              required 
              type="text" 
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleInputChange}
              placeholder="Mobile number" 
            />
          </div>

          <div className="col-md-6">
            <label className="text-16 lh-1 fw-500 text-dark-1 mb-10">
              Address Line
            </label>

            <input 
              required 
              type="text" 
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Address Line 1" 
            />
          </div>

          <div className="col-md-6">
            <label className="text-16 lh-1 fw-500 text-dark-1 mb-10">
              State
            </label>

            <input 
              required 
              type="text" 
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              placeholder="State" 
            />
          </div>

          <div className="col-md-6">
            <label className="text-16 lh-1 fw-500 text-dark-1 mb-10">
              Country
            </label>

            <input 
              required 
              type="text" 
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              placeholder="Country" 
            />
          </div>

          <div className="col-12">
            <label className="text-16 lh-1 fw-500 text-dark-1 mb-10">
              Personal info
            </label>

            <textarea 
              required 
              name="personalInfo"
              value={formData.personalInfo}
              onChange={handleInputChange}
              placeholder="Text..." 
              rows="7"
            ></textarea>
          </div>

          <div className="col-12">
            {message.text && (
              <div style={{
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
                color: message.type === 'success' ? '#155724' : '#721c24',
                border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
              }}>
                {message.text}
              </div>
            )}
            <button 
              className="button -md -purple-1 text-white"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}